import { useState, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { shiftApi, posApi, authApi } from '@/services/api';
import { createExpenseRouted, isLocalFirstMode, listLocalExpenses } from '@/offline';
import { useShiftStore } from '@/store/shiftStore';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { canAddExpenses } from '@/utils/staffPermissions';
import type { POSShift, POSExpense, ShiftCloseReport } from '@/types';
import { Clock, LogOut, Loader2, X, AlertTriangle, Lock, PlayCircle, Plus, RotateCcw, Printer } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import ShiftStartForm from '@/components/pos/ShiftStartForm';
import CashMovementModal from '@/components/pos/CashMovementModal';
import DenominationCounter from '@/components/pos/DenominationCounter';
import toast from 'react-hot-toast';
import { useSyncStore } from '@/store/syncStore';
import { browserPrintShiftReceipt } from '@/utils/shiftReceiptPrint';
import { openCashDrawer } from '@/utils/thermalPrinterUtils';
import { useShiftBarActionsStore } from '@/store/shiftBarActionsStore';
import { isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';

type Props = {
  shift: POSShift | null;
  onStartShift?: () => void;
  forceShowClose?: boolean;
  onForceCloseHandled?: () => void;
  onCloseModalChange?: (open: boolean) => void;
};

const DRAWER_EXPENSE_CATEGORIES = [
  'Tea',
  'Lunch',
  'Water',
  'Cleaning',
  'Stationery',
  'Local Transport',
  'Loading/Unloading',
  'Minor Repairs',
  'Other',
] as const;

export default function ShiftStatusBar({ shift, onStartShift, forceShowClose, onForceCloseHandled, onCloseModalChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, permissions, clearAuth } = useAuthStore();
  const userCanAddExpenses = canAddExpenses(role, permissions);
  const [showClose, setShowClose] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [closingAmount, setClosingAmount] = useState('');
  const [error, setError] = useState('');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseRemarks, setExpenseRemarks] = useState('');
  const [expenseError, setExpenseError] = useState('');
  const [submittingExpense, setSubmittingExpense] = useState(false);
  const [cashHandover, setCashHandover] = useState('');
  const [closeRemarks, setCloseRemarks] = useState('');
  const [cashMovementType, setCashMovementType] = useState<'Paid In' | 'Paid Out' | null>(null);
  const [countSubmitted, setCountSubmitted] = useState(false);

  const clearOpenShift = useShiftStore((s) => s.clearOpenShift);
  const setShiftJustClosed = useShiftStore((s) => s.setShiftJustClosed);
  const setPendingPrintShiftName = useShiftStore((s) => s.setPendingPrintShiftName);
  const qc = useQueryClient();
  const setLocked = useUIStore((s) => s.setLocked);
  const clearLocalUnlock = useDesktopSessionStore((s) => s.clearLocalUnlock);

  const logoutAfterShiftClose = async () => {
    try {
      await authApi.logout();
    } catch {
      /* offline ok */
    }
    clearLocalUnlock();
    clearAuth();
    if (isDesktopApp()) {
      const setupDone = useDesktopSessionStore.getState().setupComplete;
      navigate(setupDone ? '/unlock' : '/login', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    onCloseModalChange?.(showClose);
  }, [showClose, onCloseModalChange]);

  useEffect(() => {
    if (forceShowClose && shift) {
      setShowClose(true);
      setError('');
      void qc.invalidateQueries({ queryKey: ['shiftSummary', shift.name] });
      onForceCloseHandled?.();
    }
  }, [forceShowClose, shift, qc, onForceCloseHandled]);

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as { open_drawer_on_cash_movement?: boolean };
    },
    staleTime: 60_000,
  });

  const handleCloseDenomTotal = useCallback((total: number) => {
    setClosingAmount(String(total));
    setCountSubmitted(true);
  }, []);

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setExpenseError('');
    const amt = parseFloat(expenseAmount);
    if (isNaN(amt) || amt <= 0) {
      setExpenseError(t('expenseModal.amountRequired'));
      return;
    }
    setSubmittingExpense(true);
    try {
      await createExpenseRouted({
        category: expenseCategory,
        amount: amt,
        remarks: expenseRemarks,
        payment_method: 'Cash',
        cash_pocket: shift?.cash_pocket,
      });
      setShowAddExpense(false);
      setExpenseAmount('');
      setExpenseCategory('');
      setExpenseRemarks('');
      setExpenseError('');
      void qc.invalidateQueries({ queryKey: ['shiftSummary', shift?.name] });
      void qc.invalidateQueries({ queryKey: ['posExpenses'] });
      void qc.invalidateQueries({ queryKey: ['todayExpenses'] });
      toast.success(t('shift.expenseRecorded'));
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || t('expenseModal.failed');
      setExpenseError(msg);
    } finally {
      setSubmittingExpense(false);
    }
  };

  const [showTodayExpenses, setShowTodayExpenses] = useState(false);
  const [reversalTarget, setReversalTarget] = useState<POSExpense | null>(null);
  const [reversalReason, setReversalReason] = useState('');
  const [reversalError, setReversalError] = useState('');
  const [reversing, setReversing] = useState(false);
  const [printingX, setPrintingX] = useState(false);
  const setShiftBarActions = useShiftBarActionsStore((s) => s.setActions);

  const handlePrintX = async () => {
    if (!shift?.name || printingX) return;
    if (isLocalFirstMode() && (!navigator.onLine || !useSyncStore.getState().isServerReachable)) {
      toast.error(t('xReport.failed'));
      return;
    }
    setPrintingX(true);
    try {
      const res = await shiftApi.getXReport({ shift: shift.name, detail_type: 'invoice' });
      const report = res.data.message as ShiftCloseReport;
      browserPrintShiftReceipt({ ...report, report_type: 'X' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('xReport.failed');
      toast.error(msg);
    } finally {
      setPrintingX(false);
    }
  };

  const { data: todayExpenses = [], refetch: refetchTodayExpenses } = useQuery({
    queryKey: ['todayExpenses', shift?.name, useSyncStore.getState().posSyncMode],
    queryFn: async () => {
      if (!shift) return [];
      if (isLocalFirstMode()) {
        const rows = await listLocalExpenses();
        const today = new Date().toISOString().slice(0, 10);
        return rows
          .filter((e) => e.createdAt.slice(0, 10) === today)
          .map((e) => ({
            name: e.serverName || e.clientOfflineId,
            posting_date: e.createdAt.slice(0, 10),
            amount: e.amount,
            category: e.category,
            payment_method: e.payment_method,
            remarks: e.remarks,
            status: e.status || 'Submitted',
          })) as POSExpense[];
      }
      const res = await posApi.getPOSExpenses({ pos_shift: shift.name });
      return res.data.message as POSExpense[];
    },
    enabled: !!shift && showTodayExpenses,
  });

  useEffect(() => {
    if (!shift || shift.status === 'Pending Approval') {
      setShiftBarActions(null);
      return;
    }
    setShiftBarActions({
      shiftOpen: true,
      userCanAddExpenses,
      printingX,
      openPaidIn: () => setCashMovementType('Paid In'),
      openPaidOut: () => setCashMovementType('Paid Out'),
      openDrawerExpense: () => {
        setShowAddExpense(true);
        setExpenseError('');
      },
      openTodayExpenses: () => {
        setShowTodayExpenses(true);
        void refetchTodayExpenses();
      },
      lockScreen: () => setLocked(true),
      printX: () => void handlePrintX(),
      endShift: () => {
        setShowClose(true);
        setError('');
        void qc.invalidateQueries({ queryKey: ['shiftSummary', shift.name] });
      },
    });
    return () => setShiftBarActions(null);
  }, [shift, userCanAddExpenses, printingX, setShiftBarActions, setLocked, qc, refetchTodayExpenses]);

  const handleReverseExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setReversalError('');
    if (!reversalReason.trim()) {
      setReversalError(t('shift.reversalReasonRequired'));
      return;
    }
    if (isLocalFirstMode() && (!navigator.onLine || !useSyncStore.getState().isServerReachable)) {
      setReversalError(t('shift.expenseReverseOffline'));
      return;
    }
    setReversing(true);
    try {
      await posApi.cancelPOSExpense({
        name: reversalTarget!.name!,
        cancellation_reason: reversalReason.trim(),
      });
      setReversalTarget(null);
      setReversalReason('');
      void qc.invalidateQueries({ queryKey: ['shiftSummary', shift?.name] });
      void refetchTodayExpenses();
      toast.success(t('shift.expenseReversed'));
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || t('shift.expenseReverseFailed');
      setReversalError(msg);
    } finally {
      setReversing(false);
    }
  };

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['shiftSummary', shift?.name],
    queryFn: async () => {
      if (!shift) return null;
      const res = await shiftApi.getSummary(shift.name);
      return res.data.message as POSShift;
    },
    enabled: showClose,
  });

  const closeMutation = useMutation({
    mutationFn: () =>
      shiftApi.closeShift({
        shift: shift!.name,
        closing_amount: parseFloat(closingAmount) || 0,
        cash_handover: parseFloat(cashHandover) || 0,
        remarks: closeRemarks.trim(),
      }),
    onSuccess: async (res) => {
      const closed = res.data.message as POSShift;
      const closedName = shift!.name;
      if (closed.status === 'Pending Approval') {
        toast.success(t('shift.submittedForApproval'));
        clearOpenShift();
        qc.invalidateQueries({ queryKey: ['openShift'] });
        setShowClose(false);
        setClosingAmount('');
        setCashHandover('');
        setCloseRemarks('');
        setCountSubmitted(false);
        await logoutAfterShiftClose();
        return;
      }
      setShiftJustClosed(true);
      setPendingPrintShiftName(closedName);
      clearOpenShift();
      qc.invalidateQueries({ queryKey: ['openShift'] });
      setShowClose(false);
      setClosingAmount('');
      setCashHandover('');
      setCloseRemarks('');
      setCountSubmitted(false);
      await logoutAfterShiftClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t('shift.closeFailed'));
    },
  });

  const handleClose = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (summary && !(summary as POSShift & { expected_cash_hidden?: boolean }).expected_cash_hidden) {
      const expected = summary.expected_cash || 0;
      const counted = parseFloat(closingAmount) || 0;
      if (expected !== counted && !closeRemarks.trim()) {
        setError(t('shift.varianceCommentsRequired'));
        return;
      }
    }

    closeMutation.mutate();
  };

  if (!shift) {
    return (
      <>
        <div className="bg-yellow-50 border-b border-yellow-100 px-6 py-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-yellow-800">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-medium">{t('pos.noActiveShift')}</span>
            <span className="text-yellow-600">· {t('shift.browsingWithoutShift')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowStart(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-yellow-700 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-50 transition-colors"
            >
              <PlayCircle className="w-4 h-4" /> {t('shift.startShift')}
            </button>
          </div>
        </div>
        {showStart && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md">
              <ShiftStartForm
                onStarted={() => {
                  setShowStart(false);
                  if (onStartShift) onStartShift();
                }}
                onCancel={() => setShowStart(false)}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (shift.status === 'Pending Approval') {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-900">
        <Trans i18nKey="shift.pendingApproval" values={{ name: shift.name }} />
      </div>
    );
  }

  return (
    <>
      {shift.is_expiring_soon && !shift.is_expired && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-900">
          {t('shift.expiringSoon', {
            hours: shift.hours_open?.toFixed(1),
            maxHours: shift.max_shift_hours ?? 24,
          })}
        </div>
      )}
      <div className="hidden lg:flex bg-primary-50 border-b border-primary-100 px-6 py-2 flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-primary-800">
          <Clock className="w-4 h-4" />
          <span className="font-medium">{t('pos.shiftOpen')}</span>
          <span className="text-primary-600">· {shift.pocket_name}</span>
          <span className="text-primary-600">· {t('pos.openingAmount', { amount: formatMoney(shift.opening_amount) })}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCashMovementType('Paid In')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-800 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('shift.paidIn')}
          </button>
          <button
            type="button"
            onClick={() => setCashMovementType('Paid Out')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-800 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
          >
            <PakRupeeIcon className="w-4 h-4" /> {t('shift.paidOut')}
          </button>
          {userCanAddExpenses && (
            <>
              <button
                type="button"
                onClick={() => { setShowAddExpense(true); setExpenseError(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
              >
                <Plus className="w-4 h-4" /> {t('shift.addDrawerExpense')}
              </button>
              <button
                type="button"
                onClick={() => { setShowTodayExpenses(true); void refetchTodayExpenses(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-750 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <PakRupeeIcon className="w-4 h-4" /> {t('shift.todaysExpenses')}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setLocked(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <Lock className="w-4 h-4" /> {t('shift.lockScreen')}
          </button>
          <button
            type="button"
            onClick={() => void handlePrintX()}
            disabled={printingX}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-60"
          >
            {printingX ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            {t('xReport.printX')}
          </button>
          <button
            type="button"
            onClick={() => { 
              setShowClose(true); 
              setError(''); 
              void qc.invalidateQueries({ queryKey: ['shiftSummary', shift?.name] });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" /> {t('shift.endShift')}
          </button>
        </div>
      </div>

      {showClose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">{t('shift.closeTitle')}</h3>
              <button type="button" onClick={() => setShowClose(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleClose} className="p-6 space-y-4">
              {summaryLoading || qc.isFetching({ queryKey: ['shiftSummary', shift?.name] }) ? (
                <div className="flex items-center gap-2 text-gray-500 py-4">
                  <Loader2 className="w-5 h-5 animate-spin" /> {t('shift.loadingSummary')}
                </div>
              ) : summary ? (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">{t('shift.invoices')}</span><span>{summary.invoice_count}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">{t('shift.totalSales')}</span><span>{formatMoney(summary.total_sales || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">{t('shift.cashSales')}</span><span>{formatMoney(summary.cash_sales || 0)}</span></div>
                  {(summary.cash_returns || 0) > 0 && (
                    <div className="flex justify-between text-amber-700">
                      <span className="text-gray-500">{t('shift.cashReturns')}</span>
                      <span>{formatMoney(summary.cash_returns || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-red-600"><span className="text-gray-500">{t('shift.drawerExpenses')}</span><span>{formatMoney(summary.total_drawer_expenses || 0)}</span></div>
                  {!(summary as POSShift & { expected_cash_hidden?: boolean }).expected_cash_hidden && !countSubmitted ? (
                    <div className="text-xs text-gray-500 italic py-1">{t('shift.countCashHint')}</div>
                  ) : null}
                  {(!(summary as POSShift & { expected_cash_hidden?: boolean }).expected_cash_hidden || countSubmitted) && (
                    <div className="flex justify-between font-semibold text-gray-800 border-t border-gray-200 pt-2"><span className="text-gray-500">{t('shift.expectedCash')}</span><span>{formatMoney(summary.expected_cash || 0)}</span></div>
                  )}
                  
                  {closingAmount && (!(summary as POSShift & { expected_cash_hidden?: boolean }).expected_cash_hidden || countSubmitted) && (
                    <div className="flex justify-between font-semibold border-t border-gray-150 pt-2">
                      <span className="text-gray-500">{t('shift.difference')}</span>
                      <span className={
                        (Number(closingAmount) - (summary.expected_cash || 0)) === 0
                          ? 'text-emerald-600'
                          : (Number(closingAmount) - (summary.expected_cash || 0)) > 0
                            ? 'text-amber-600'
                            : 'text-red-650'
                      }>
                        {formatMoney(Number(closingAmount) - (summary.expected_cash || 0))}
                      </span>
                    </div>
                  )}
                </div>
              ) : null}
              <DenominationCounter onTotalChange={handleCloseDenomTotal} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.closingCashCount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={closingAmount}
                    onChange={(e) => setClosingAmount(e.target.value)}
                    placeholder={t('shift.closingCashPlaceholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.cashHandover')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={cashHandover}
                    onChange={(e) => setCashHandover(e.target.value)}
                    placeholder={t('shift.cashHandoverPlaceholder')}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shift.comments')} {summary && (Number(closingAmount) - (summary.expected_cash || 0)) !== 0 ? <span className="text-red-500">{t('shift.commentsRequired')}</span> : <span className="text-gray-400 font-normal">{t('shift.commentsOptional')}</span>}
                  </label>
                  <textarea
                    className="input-field min-h-[60px]"
                    value={closeRemarks}
                    onChange={(e) => setCloseRemarks(e.target.value)}
                    placeholder={t('shift.commentsPlaceholder')}
                    required={summary ? (Number(closingAmount) - (summary.expected_cash || 0)) !== 0 : false}
                  />
                </div>
              </div>
              {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {error}
                </p>
              )}
              <div className="flex gap-3">
                <button type="submit" disabled={closeMutation.isPending} className="btn-primary flex-1">
                  {closeMutation.isPending ? t('pos.closingShift') : t('pos.confirmCloseAndLogout')}
                </button>
                <button type="button" onClick={() => setShowClose(false)} className="btn-secondary">{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">{t('shift.addDrawerExpense')}</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddExpense(false);
                  setExpenseAmount('');
                  setExpenseCategory('');
                  setExpenseRemarks('');
                  setExpenseError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleExpenseSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.expenseCategory')}</label>
                <select
                  required
                  className="input-field"
                  value={expenseCategory}
                  onChange={(e) => setExpenseCategory(e.target.value)}
                >
                  <option value="">{t('expenseModal.selectCategory')}</option>
                  {DRAWER_EXPENSE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {t(`expenseCategories.${cat}`, { defaultValue: cat })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.expenseAmount')} ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="input-field font-mono"
                  placeholder="0.00"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.expenseRemarks')}</label>
                <textarea
                  className="input-field min-h-[80px]"
                  placeholder={t('expenseModal.remarksPlaceholder')}
                  value={expenseRemarks}
                  onChange={(e) => setExpenseRemarks(e.target.value)}
                />
              </div>

              {expenseError && (
                <p className="text-sm text-red-650 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {expenseError}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submittingExpense}
                  className="btn-primary flex-1"
                >
                  {submittingExpense ? t('shift.addingExpense') : `${t('common.add')} ${t('pos.expense')}`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddExpense(false);
                    setExpenseAmount('');
                    setExpenseCategory('');
                    setExpenseRemarks('');
                    setExpenseError('');
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTodayExpenses && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-950 text-lg">{t('shift.todayExpensesTitle')}</h3>
                <p className="text-xs text-gray-500">{t('shift.todayExpensesSubtitle')}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowTodayExpenses(false);
                  setReversalTarget(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[400px]">
              {todayExpenses.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-500 text-sm">{t('shift.noExpensesToday')}</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="pb-3 px-2">{t('shift.expenseCategory')}</th>
                      <th className="pb-3 px-2">{t('shift.expenseRemarks')}</th>
                      <th className="pb-3 px-2">{t('shift.expenseAmount')}</th>
                      <th className="pb-3 px-2">{t('shift.expenseStatus')}</th>
                      <th className="pb-3 px-2 text-right">{t('shift.expenseActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {todayExpenses.map((exp) => (
                      <tr key={exp.name} className="hover:bg-gray-50/50">
                        <td className="py-3 px-2">
                          <span className="font-medium text-gray-900">
                            {t(`expenseCategories.${exp.category}`, { defaultValue: exp.category })}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-gray-500 max-w-[150px] truncate" title={exp.remarks}>
                          {exp.remarks || '-'}
                        </td>
                        <td className="py-3 px-2 font-semibold font-mono text-gray-900">
                          {formatMoney(exp.amount)}
                        </td>
                        <td className="py-3 px-2">
                          {exp.status === 'Submitted' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-100">
                              {t('shift.expenseActive')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-100">
                              {t('shift.expenseReversedStatus')}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {exp.status === 'Submitted' && (
                            <button
                              type="button"
                              onClick={() => {
                                setReversalTarget(exp);
                                setReversalReason('');
                                setReversalError('');
                              }}
                              className="text-red-650 hover:text-red-800 text-xs font-medium py-1 px-2 hover:bg-red-50 rounded flex items-center gap-1"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> {t('shift.reverseExpense')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowTodayExpenses(false);
                  setReversalTarget(null);
                }}
                className="btn-secondary"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {reversalTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-red-50">
              <h3 className="font-semibold text-red-950 text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-650" /> {t('shift.reverseExpenseTitle')}
              </h3>
              <button
                type="button"
                onClick={() => setReversalTarget(null)}
                className="text-red-850 hover:text-red-950 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleReverseExpenseSubmit} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {t('shift.reverseExpenseConfirm', { amount: formatMoney(reversalTarget.amount) })}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('shift.reverseReason')}</label>
                <textarea
                  required
                  className="input-field border-red-250 focus:ring-red-200"
                  placeholder={t('shift.reverseReasonPlaceholder')}
                  value={reversalReason}
                  onChange={(e) => setReversalReason(e.target.value)}
                />
              </div>
              {reversalError && (
                <p className="text-sm text-red-650 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {reversalError}
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={reversing}
                  className="bg-red-650 hover:bg-red-700 text-white font-medium px-4 py-2.5 rounded-xl flex-1 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {reversing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('shift.confirmReverse')}
                </button>
                <button
                  type="button"
                  onClick={() => setReversalTarget(null)}
                  className="btn-secondary py-2.5"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cashMovementType && (
        <CashMovementModal
          open
          movementType={cashMovementType}
          shiftName={shift.name}
          cashPocket={shift.cash_pocket}
          pocketName={shift.pocket_name}
          onClose={() => setCashMovementType(null)}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ['shiftSummary', shift.name] });
            if (posSettings?.open_drawer_on_cash_movement) {
              void openCashDrawer();
            }
          }}
        />
      )}
    </>
  );
}
